import type { ReactNode } from 'react';

import { LanguageSwitcher } from '@/components/LanguageSwitcher';

interface AuthShellProps {
  /** Left-panel hero content — wordmark, tagline, feature list, stepper, etc. Optional on mobile. */
  hero?: ReactNode;
  /** Card title — rendered in Inter Bold 26px, not the Instrument Serif that global.css applies to h1/h2/h3. */
  title: string;
  /** Card sub-heading — Inter 14px, muted. */
  description?: string;
  /** Card body — form content. */
  children: ReactNode;
  /** Optional post-card footer slot (e.g. terms of service). */
  footer?: ReactNode;
}

/**
 * AuthShell — split-hero layout used by all auth screens.
 *
 * Layout (≥1024px): 60% left branded hero + 40% right form column with a
 * 420px-wide elevated card. Below 1024px collapses into a single column
 * with a compact wordmark header.
 *
 * The hero background uses two layered radial gradients to evoke the
 * warm halo from the Figma design (bg/surface + bg/surface-raised ellipses)
 * without the extra DOM of absolutely-positioned ellipse divs.
 *
 * Heading text uses a plain div to avoid the Instrument Serif h1/h2/h3 base
 * rule — only the wordmark in the hero slot is meant to be serif.
 */
export function AuthShell({ hero, title, description, children, footer }: AuthShellProps) {
  return (
    <main className="relative flex min-h-svh flex-col bg-background text-foreground">
      <div className="flex flex-1 flex-col lg:flex-row">
        {/* Left branded hero — visible ≥ lg */}
        <aside
          className="relative hidden overflow-hidden lg:flex lg:w-[60%] lg:items-center lg:justify-center"
          style={{
            backgroundImage:
              'radial-gradient(circle at 18% 52%, var(--bg-surface-raised) 0%, transparent 35%), radial-gradient(circle at 22% 55%, var(--bg-surface) 0%, transparent 55%)',
          }}
        >
          <div className="relative z-10 mx-auto w-full max-w-xl px-20">{hero}</div>
        </aside>

        {/* Divider — visible ≥ lg */}
        <div className="hidden w-px bg-[var(--border-default)] lg:block" />

        {/* Right form column */}
        <section className="flex flex-1 items-center justify-center p-6 lg:p-10">
          <div className="flex w-full max-w-md flex-col gap-6">
            {/* Compact wordmark header for mobile — hidden ≥ lg because hero handles it */}
            <div className="flex flex-col items-center gap-1 lg:hidden">
              <span
                className="text-3xl leading-none tracking-tight text-[var(--text-accent)] brand-name"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                Secure Labs
              </span>
              <span className="text-xs text-[var(--text-secondary)]">Estate Access Control</span>
            </div>

            {/* Form card */}
            <div className="card-brand p-8 md:p-10">
              <div className="mb-6">
                <div className="heading-card text-[var(--text-primary)]">{title}</div>
                {description && (
                  <p className="mt-1.5 text-sm text-[var(--text-secondary)]">{description}</p>
                )}
              </div>
              {children}
            </div>

            {footer && <div>{footer}</div>}
          </div>
        </section>
      </div>

      <footer className="flex flex-col items-center gap-3 py-4 text-center text-[11px] text-[var(--text-tertiary)]">
        <LanguageSwitcher />
        <span>© 2026 Secure Labs · All rights reserved</span>
      </footer>
    </main>
  );
}
